import React, { useState } from "react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { StatusBadge, usePoll, fmtUSDT, fmtINR } from "@/components/common";
import { Search, ShieldOff, ShieldCheck, Ban, Snowflake, X, History } from "lucide-react";

export default function AdminCustomers() {
  const [q, setQ] = useState("");
  const [customers, setCustomers] = useState([]);
  const [detail, setDetail] = useState(null);
  const [busy, setBusy] = useState(false);
  const [blockForm, setBlockForm] = useState({ reason: "", permanent: false, duration_hours: "" });
  const [unblockReason, setUnblockReason] = useState("");

  const load = () => api.get(`/admin/customers?q=${encodeURIComponent(q)}`).then((r) => setCustomers(r.data)).catch(() => {});
  usePoll(load, 10000, [q]);

  const openDetail = async (c) => {
    try { const { data } = await api.get(`/admin/customers/${c.id}`); setDetail(data); setBlockForm({ reason: "", permanent: false, duration_hours: "" }); setUnblockReason(""); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const refreshDetail = async () => { if (detail) { const { data } = await api.get(`/admin/customers/${detail.customer.id}`); setDetail(data); } load(); };

  const cid = detail?.customer?.id;
  const cst = detail?.customer;

  const block = async () => {
    if (!blockForm.reason.trim()) return toast.error("Reason required");
    setBusy(true);
    try {
      await api.post(`/admin/customers/${cid}/block`, {
        reason: blockForm.reason, permanent: blockForm.permanent,
        duration_hours: blockForm.duration_hours ? parseFloat(blockForm.duration_hours) : null,
      });
      toast.success("Customer blocked"); await refreshDetail();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  const unblock = async () => {
    if (!unblockReason.trim()) return toast.error("Reason required");
    setBusy(true);
    try { await api.post(`/admin/customers/${cid}/unblock`, { reason: unblockReason }); toast.success("Customer reactivated"); await refreshDetail(); }
    catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  const restrict = async (patch) => {
    const reason = blockForm.reason.trim() || window.prompt("Reason for this change?");
    if (!reason) return;
    setBusy(true);
    try { await api.post(`/admin/customers/${cid}/restrictions`, { reason, ...patch }); toast.success("Updated"); await refreshDetail(); }
    catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  const isBlocked = cst && ["temporarily_blocked", "suspended", "deactivated"].includes(cst.status);

  return (
    <div className="space-y-6">
      <h1 className="font-heading text-2xl sm:text-3xl font-bold text-slate-900">Customers</h1>
      <div className="relative max-w-md">
        <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search by name or email" data-testid="customer-search-input"
          className="w-full pl-10 pr-4 py-2.5 rounded-xl border border-slate-300 outline-none focus:border-blue-500" />
      </div>

      <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden">
        <div className="overflow-x-auto scrollbar-thin">
          <table className="w-full text-sm" data-testid="customers-table">
            <thead><tr className="text-left text-xs uppercase tracking-wider text-slate-400 bg-slate-50 border-b border-slate-200">
              <th className="py-3 px-4">Name</th><th className="py-3 px-4">Email</th><th className="py-3 px-4">Available</th><th className="py-3 px-4">Held</th><th className="py-3 px-4">Deficit</th><th className="py-3 px-4">Status</th><th className="py-3 px-4">Flags</th><th className="py-3 px-4"></th>
            </tr></thead>
            <tbody>
              {customers.map((c) => (
                <tr key={c.id} className="border-b border-slate-100" data-testid={`customer-row-${c.email}`}>
                  <td className="py-3 px-4 font-medium text-slate-800">{c.name}</td>
                  <td className="py-3 px-4 text-slate-500">{c.email}</td>
                  <td className="py-3 px-4 font-mono">{fmtUSDT(c.wallet.available)}</td>
                  <td className="py-3 px-4 font-mono text-amber-700">{fmtUSDT(c.wallet.held)}</td>
                  <td className={`py-3 px-4 font-mono ${parseFloat(c.wallet.deficit) > 0 ? "text-rose-600" : "text-slate-400"}`}>{fmtUSDT(c.wallet.deficit)}</td>
                  <td className="py-3 px-4"><StatusBadge status={c.status} /></td>
                  <td className="py-3 px-4 text-xs">
                    {c.freeze_sell && <span className="inline-block bg-rose-100 text-rose-700 rounded px-1.5 py-0.5 mr-1">no-sell</span>}
                    {c.freeze_deposits && <span className="inline-block bg-rose-100 text-rose-700 rounded px-1.5 py-0.5">no-deposit</span>}
                  </td>
                  <td className="py-3 px-4">
                    <button onClick={() => openDetail(c)} data-testid={`manage-customer-${c.email}`}
                      className="px-3 py-1.5 rounded-lg bg-blue-600 text-white text-xs font-semibold hover:bg-blue-700">Manage</button>
                  </td>
                </tr>
              ))}
              {customers.length === 0 && <tr><td colSpan={8} className="py-10 text-center text-slate-400">No customers found.</td></tr>}
            </tbody>
          </table>
        </div>
      </div>

      {detail && (
        <div className="fixed inset-0 z-50 flex justify-end bg-slate-900/40" onClick={() => setDetail(null)}>
          <div className="bg-white w-full max-w-xl h-full overflow-y-auto scrollbar-thin p-6 space-y-5" onClick={(e) => e.stopPropagation()} data-testid="customer-manage-panel">
            <div className="flex items-start justify-between">
              <div>
                <h2 className="font-heading font-bold text-xl text-slate-900">{cst.name}</h2>
                <div className="text-sm text-slate-500">{cst.email}</div>
                {cst.phone && <div className="text-sm text-slate-500" data-testid="manage-customer-phone">Mobile: {cst.phone_country_code} {cst.phone}</div>}
                <div className="mt-2"><StatusBadge status={cst.status} testid="manage-status-badge" /></div>
                {cst.block_reason && <div className="text-xs text-rose-600 mt-1">Reason: {cst.block_reason}{cst.block_expires_at ? ` · until ${new Date(cst.block_expires_at).toLocaleString()}` : ""}</div>}
              </div>
              <button onClick={() => setDetail(null)} className="p-2 hover:bg-slate-100 rounded-lg"><X className="w-5 h-5" /></button>
            </div>

            <div className="grid grid-cols-3 gap-3 text-sm">
              <div className="bg-blue-50 rounded-lg p-3"><div className="text-xs text-slate-500">Available</div><div className="font-mono font-bold text-blue-700">{fmtUSDT(detail.wallet.available)}</div></div>
              <div className="bg-amber-50 rounded-lg p-3"><div className="text-xs text-slate-500">Held</div><div className="font-mono font-bold text-amber-700">{fmtUSDT(detail.wallet.held)}</div></div>
              <div className="bg-rose-50 rounded-lg p-3"><div className="text-xs text-slate-500">Deficit</div><div className="font-mono font-bold text-rose-700">{fmtUSDT(detail.wallet.deficit)}</div></div>
            </div>

            <div className="border border-slate-200 rounded-2xl p-4 space-y-3">
              <h3 className="font-semibold text-slate-900 text-sm">Account controls</h3>
              <textarea value={blockForm.reason} onChange={(e) => setBlockForm({ ...blockForm, reason: e.target.value })}
                placeholder="Reason (required for block)" rows={2} data-testid="block-reason-input"
                className="w-full px-3 py-2 rounded-xl border border-slate-300 outline-none text-sm" />

              {!isBlocked ? (
                <div className="space-y-3">
                  <div className="flex items-center gap-3">
                    <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={blockForm.permanent} onChange={(e) => setBlockForm({ ...blockForm, permanent: e.target.checked })} data-testid="block-permanent-toggle" /> Permanent suspension</label>
                    {!blockForm.permanent && (
                      <input type="number" min="0" step="0.5" value={blockForm.duration_hours} onChange={(e) => setBlockForm({ ...blockForm, duration_hours: e.target.value })}
                        placeholder="hours (blank = indefinite)" data-testid="block-duration-input" className="flex-1 px-3 py-1.5 rounded-lg border border-slate-300 text-sm outline-none" />
                    )}
                  </div>
                  <button onClick={block} disabled={busy} data-testid="block-customer-button"
                    className="w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl bg-rose-600 text-white font-semibold hover:bg-rose-700 disabled:opacity-60">
                    <Ban className="w-4 h-4" /> {blockForm.permanent ? "Suspend account" : "Block customer"}
                  </button>
                </div>
              ) : (
                <div className="space-y-3">
                  <input value={unblockReason} onChange={(e) => setUnblockReason(e.target.value)} placeholder="Reason for unblock" data-testid="unblock-reason-input"
                    className="w-full px-3 py-2 rounded-xl border border-slate-300 outline-none text-sm" />
                  <button onClick={unblock} disabled={busy} data-testid="unblock-customer-button"
                    className="w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl bg-emerald-600 text-white font-semibold hover:bg-emerald-700 disabled:opacity-60">
                    <ShieldCheck className="w-4 h-4" /> Unblock / reactivate
                  </button>
                </div>
              )}

              <div className="border-t border-slate-200 pt-3 grid grid-cols-2 gap-2">
                <button onClick={() => restrict({ freeze_sell: !cst.freeze_sell })} disabled={busy} data-testid="toggle-freeze-sell"
                  className={`inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-xl text-xs font-semibold border ${cst.freeze_sell ? "bg-rose-50 border-rose-300 text-rose-700" : "border-slate-300 text-slate-600"}`}>
                  <Snowflake className="w-3.5 h-3.5" /> {cst.freeze_sell ? "Unfreeze sells" : "Freeze sells"}
                </button>
                <button onClick={() => restrict({ freeze_deposits: !cst.freeze_deposits })} disabled={busy} data-testid="toggle-freeze-deposits"
                  className={`inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-xl text-xs font-semibold border ${cst.freeze_deposits ? "bg-rose-50 border-rose-300 text-rose-700" : "border-slate-300 text-slate-600"}`}>
                  <ShieldOff className="w-3.5 h-3.5" /> {cst.freeze_deposits ? "Allow deposits" : "Restrict deposits"}
                </button>
                {cst.status === "active" && (
                  <button onClick={() => restrict({ status: "deactivated" })} disabled={busy} data-testid="deactivate-customer"
                    className="col-span-2 px-3 py-2 rounded-xl text-xs font-semibold border border-slate-300 text-slate-600">Deactivate account</button>
                )}
                {cst.status === "deactivated" && (
                  <button onClick={() => restrict({ status: "active" })} disabled={busy} data-testid="reactivate-customer"
                    className="col-span-2 px-3 py-2 rounded-xl text-xs font-semibold border border-emerald-300 text-emerald-700">Reactivate account</button>
                )}
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3 text-sm">
              <Mini label="Deposits" value={detail.deposits.length} />
              <Mini label="Orders" value={detail.orders.length} />
              <Mini label="Payouts" value={detail.payouts.length} />
              <Mini label="Disputes" value={detail.disputes.length} tone={detail.disputes.length ? "rose" : ""} />
              <Mini label="Tickets" value={detail.tickets.length} />
              <Mini label="Rate override" value={detail.rate_override ? fmtINR(detail.rate_override) : "—"} />
            </div>

            <div className="border border-slate-200 rounded-2xl p-4">
              <h3 className="font-semibold text-slate-900 text-sm flex items-center gap-1.5 mb-2"><History className="w-4 h-4" /> Restriction history</h3>
              {(!cst.status_history || cst.status_history.length === 0) ? <div className="text-xs text-slate-400">No status changes recorded.</div> : (
                <div className="space-y-1.5" data-testid="status-history">
                  {cst.status_history.slice().reverse().map((h, i) => (
                    <div key={i} className="text-xs text-slate-600 border-b border-slate-100 pb-1.5">
                      <span className="font-medium">{h.from || "—"} → {h.to}</span> · {h.reason} · <span className="text-slate-400">{h.actor} · {new Date(h.at).toLocaleString()}</span>
                      {h.expires_at && <span className="text-amber-600"> · until {new Date(h.expires_at).toLocaleString()}</span>}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Mini({ label, value, tone }) {
  return <div className={`rounded-lg p-3 border ${tone === "rose" ? "bg-rose-50 border-rose-200" : "bg-slate-50 border-slate-200"}`}>
    <div className="text-xs text-slate-500">{label}</div><div className="font-mono font-bold text-slate-900">{value}</div></div>;
}
